import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getData, postData, putData, removeData } from "./useFetcher";
import { store } from "src/redux/store";
import { setState } from "src/redux/slices/state";
import { emitSocketAck, getSocketClient } from "src/utils/socketClient";

export enum QueryKeys {
  NOTIFICATION_HOME = "notification_home",
}

interface UseServiceOptions {
  key?: QueryKeys;
  cacheMinutes?: number;
  refreshOnFocus?: boolean;
  manual?: boolean;
  fullUrl?: boolean;
  enabled?: boolean;
  socket?: boolean;
  socketRequestEvent?: string;
  socketRequestPayload?: any;
  socketInvalidationEvents?: string[];
  socketWatchEvent?: string;
  socketUnwatchEvent?: string;
  socketWatchPayload?: any;
  socketRefetchOnInvalidation?: boolean;
}

async function getRequest(url: string) {
  return fetch(url, { method: "GET" }).then((res) => res.json());
}

const useService = (url?: string, options?: UseServiceOptions) => {
  const {
    key,
    cacheMinutes,
    refreshOnFocus = false,
    manual = false,
    fullUrl = false,
    enabled = true,
    socket = false,
    socketRequestEvent,
    socketRequestPayload,
    socketInvalidationEvents,
    socketWatchEvent,
    socketUnwatchEvent,
    socketWatchPayload,
    socketRefetchOnInvalidation = true,
  } = options || {};

  const dispatch = useDispatch();
  const cachedData = useSelector((state: any) =>
    key ? state?.state?.[key] : null
  );

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  // GET state
  const [data, setData] = useState<any>(cachedData?.data || undefined);
  const [loading, setLoading] = useState<boolean>(key ? !cachedData : false);
  const [error, setError] = useState<any>(undefined);
  const hasFetchedOnce = useRef(false);
  const didMount = useRef(false);

  // POST state
  const [postDataState, setPostDataState] = useState<any>(undefined);
  const [postLoading, setPostLoading] = useState<boolean>(false);
  const [postError, setPostError] = useState<any>(undefined);

  // PUT state
  const [putDataState, setPutDataState] = useState<any>(undefined);
  const [putLoading, setPutLoading] = useState<boolean>(false);
  const [putError, setPutError] = useState<any>(undefined);

  // DELETE state
  const [removeDataState, setRemoveDataState] = useState<any>(undefined);
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);
  const [removeError, setRemoveError] = useState<any>(undefined);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(
    async (force = false) => {
      if (!url || (manual && !force)) return;

      const lastUpdated = key ? store.getState().state?.[`${key}_timestamp`] : 0;
      const isExpired = cacheMinutes
        ? Date.now() - lastUpdated > cacheMinutes * 60 * 1000
        : true;

      if (!force && key && cachedData && !isExpired) return;

      const requestId = ++requestIdRef.current;

      if (cachedData === null) setLoading(true);

      try {
        const response =
          socket && socketRequestEvent
            ? await emitSocketAck(socketRequestEvent, socketRequestPayload)
            : fullUrl
            ? await getRequest(url)
            : await getData(url);

        if (!isMountedRef.current || requestId !== requestIdRef.current) return;

        setData(response);
        setError(null);

        if (key && !fullUrl) {
          dispatch(setState({ key, value: response }));
          dispatch(setState({ key: `${key}_timestamp`, value: Date.now() }));
        }
      } catch (err) {
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        console.error("❌ [GET] Error fetching data:", {
          url,
          method: "GET",
          key,
          cacheMinutes,
          refreshOnFocus,
          manual,
          fullUrl,
          socket,
          socketRequestEvent,
          error: err,
        });
        setError(err);
      } finally {
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        setLoading(false);
      }
    },
    [
      url,
      key,
      cachedData,
      dispatch,
      cacheMinutes,
      refreshOnFocus,
      fullUrl,
      manual,
      socket,
      socketRequestEvent,
      socketRequestPayload,
    ]
  );

  // Initial fetch
  useEffect(() => {
    if (!url || manual || !enabled) return;
    if (!hasFetchedOnce.current) {
      hasFetchedOnce.current = true;
      fetchData();
    }
  }, [fetchData, manual, url, enabled]);

  // Refetch on dependency changes (for example enabled)
  useEffect(() => {
    if (didMount.current) {
      if (hasFetchedOnce.current && enabled && !manual) {
        fetchData(true);
      }
    } else {
      didMount.current = true;
    }
  }, [enabled, manual, fetchData]);

  // Refresh on tab focus
  useEffect(() => {
    if (!refreshOnFocus || manual || !url || !enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchData(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchData, refreshOnFocus, manual, url, enabled]);

  // Periodic cache verification
  useEffect(() => {
    if (!cacheMinutes || !key || manual || !url || !enabled) return;

    let timeout: NodeJS.Timeout;
    let isActive = document.visibilityState === "visible";

    const checkCache = () => {
      const lastUpdated = store.getState().state[`${key}_timestamp`] || 0;
      const isExpired = Date.now() - lastUpdated > cacheMinutes * 60 * 1000;

      if (isExpired && isActive && enabled) {
        fetchData(true);
      }

      timeout = setTimeout(checkCache, cacheMinutes * 60 * 1000);
    };

    checkCache();

    const handleVisibilityChange = () => {
      isActive = document.visibilityState === "visible";
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [key, cacheMinutes, fetchData, manual, url, enabled]);

  // Socket invalidation listeners
  useEffect(() => {
    if (
      !socket ||
      !enabled ||
      !socketRefetchOnInvalidation ||
      !socketInvalidationEvents?.length
    ) {
      return;
    }

    let client: ReturnType<typeof getSocketClient> | null = null;
    try {
      client = getSocketClient();
    } catch (err) {
      setError(err);
      return;
    }

    if (!client) return;

    const socketClient = client;

    const handleInvalidation = () => {
      fetchData(true);
    };

    socketInvalidationEvents.forEach((eventName) => {
      socketClient.on(eventName, handleInvalidation);
    });

    return () => {
      socketInvalidationEvents.forEach((eventName) => {
        socketClient.off(eventName, handleInvalidation);
      });
    };
  }, [
    socket,
    enabled,
    socketRefetchOnInvalidation,
    socketInvalidationEvents,
    fetchData,
  ]);

  // Socket watch/unwatch
  useEffect(() => {
    if (!socket || !enabled || !socketWatchEvent) return;

    let client: ReturnType<typeof getSocketClient> | null = null;
    try {
      client = getSocketClient();
    } catch (err) {
      setError(err);
      return;
    }

    if (!client) return;

    const socketClient = client;

    socketClient.emit(socketWatchEvent, socketWatchPayload || {});

    return () => {
      if (socketUnwatchEvent) {
        socketClient.emit(socketUnwatchEvent, socketWatchPayload || {});
      }
    };
  }, [
    socket,
    enabled,
    socketWatchEvent,
    socketUnwatchEvent,
    socketWatchPayload,
  ]);

  const post = async (body: any) => {
    if (!url) return;
    setPostLoading(true);

    try {
      const response = await postData(url, { arg: body });
      setPostDataState(response);
      return { data: response, error: null };
    } catch (err) {
      setPostError(err);
      return { data: null, error: err };
    } finally {
      setPostLoading(false);
    }
  };

  const put = async (body: any) => {
    if (!url) return;
    setPutLoading(true);

    try {
      const response = await putData(url, { arg: body });
      setPutDataState(response);
      return { data: response, error: null };
    } catch (err) {
      setPutError(err);
      return { data: null, error: err };
    } finally {
      setPutLoading(false);
    }
  };

  const remove = async (body: any) => {
    if (!url) return;
    setRemoveLoading(true);

    try {
      const response = await removeData(url, { arg: body });
      setRemoveDataState(response);
      return { data: response, error: null };
    } catch (err) {
      setRemoveError(err);
      return { data: null, error: err };
    } finally {
      setRemoveLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    refetch: () => url && fetchData(true), // NUNCA COLOCAR EM USEEFFECT

    post, // NUNCA COLOCAR EM USEEFFECT
    postData: postDataState,
    postLoading,
    postError,

    put, // NUNCA COLOCAR EM USEEFFECT
    putData: putDataState,
    putLoading,
    putError,

    remove, // NUNCA COLOCAR EM USEEFFECT
    removeData: removeDataState,
    removeLoading,
    removeError,
  };
};

export default useService;
